import React, { useCallback, useContext } from "react";
import { EDITOR_KEYS } from "../../../../../other/constants/EditorKeys.jsx";
import { GRANTS } from "../../../../../other/constants/PrivilegeTypes.jsx";
import BryntumContext from "../../../../context/bryntumContext.jsx";
import { SWIVU_STANDARD_ICONS } from "../../../../../other/constants/IconKeys.jsx";
import { ALERT_TYPES, VIEW_TYPES } from "../../../../../other/constants/OtherKeys.jsx";
import { canAddNewUser, openNewSpotIncentiveEditor } from "../../../../../other/utils/utils.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils.jsx";
import { Plus } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/modern/components/ui/dropdown-menu";

const AppAddButton = () => {
  const { user, editorHandler, project } = useContext(BryntumContext);

  const onCreateNewTask = useCallback(() => {
    const newRecord = project.extraTaskStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, { task: newRecord });
  }, [editorHandler, project]);

  const onCreateNewResource = useCallback(() => {
    const newRecord = project.resourceStore.createRecord({ type: null });
    editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, newRecord, { resource: newRecord });
  }, [editorHandler, project]);

  const onCreateNewClient = useCallback(() => {
    const newRecord = project.clientsStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, newRecord, { client: newRecord });
  }, [editorHandler, project]);

  const onCreateNewUser = useCallback(() => {
    const newRecord = project.boardUsersStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.userEditor, newRecord, { user: newRecord });
  }, [editorHandler, project]);

  const onCreateNewContact = useCallback(() => {
    const newRecord = project.contactsStore.createRecord();
    editorHandler.openEditor(
      EDITOR_KEYS.contactEditor,
      { id: newRecord.id },
      { contact: newRecord },
      {
        by: "recordType",
        useExisting: true
      }
    );
  }, [editorHandler, project]);

  const addNewBoardAlert = useCallback(() => {
    const newRecord = project.reminderStore.createRecord({
      alertType: ALERT_TYPES.BoardAlert
    });
    editorHandler.openEditor(EDITOR_KEYS.boardAlertEditor, null, { reminderRecord: newRecord }, { by: "recordType", useExisting: true });
  }, [editorHandler]);

  const onAddNewBudget = useCallback(() => {
    const newRecord = project.taskBudgetStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
  }, [editorHandler, project]);

  const onCreateNewOutcomeIncentive = useCallback(() => {
    const newRecord = project.outcomeIncentiveStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.outcomeIncentiveEditor, newRecord, { incentiveRecord: newRecord });
  }, [editorHandler, project]);

  const onCreateNewSpotIncentive = useCallback(() => {
    openNewSpotIncentiveEditor({ project, editorHandler });
  }, [editorHandler, project]);

  const canCreate = user && user.can(GRANTS.grant_four);
  const canCreateUser = user && canAddNewUser(user);
  const hasManagePermission = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon">
          <Plus className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem disabled={!canCreate} onSelect={onCreateNewResource}>
          <i className={`${SWIVU_STANDARD_ICONS[VIEW_TYPES.resources]} mr-2`} />
          New Resource
        </DropdownMenuItem>

        <DropdownMenuItem disabled={!canCreate} onSelect={onCreateNewTask}>
          <i className={`${SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks]} mr-2`} />
          New Task
        </DropdownMenuItem>

        <DropdownMenuItem disabled={!canCreate} onSelect={onCreateNewClient}>
          <i className={`${SWIVU_STANDARD_ICONS[VIEW_TYPES.clients]} mr-2`} />
          New Client
        </DropdownMenuItem>

        <DropdownMenuItem disabled={canCreateUser} onSelect={onCreateNewUser}>
          <i className={`${SWIVU_STANDARD_ICONS.addUser} mr-2`} />
          New User
        </DropdownMenuItem>

        <DropdownMenuItem disabled={!canCreate} onSelect={onCreateNewContact}>
          <i className={`${SWIVU_STANDARD_ICONS[VIEW_TYPES.contacts]} mr-2`} />
          New Contact
        </DropdownMenuItem>

        <DropdownMenuItem disabled={(user && !user.can(GRANTS.grant_three)) || (user && user.isDemoUser && user.isSandboxBoard)} onSelect={addNewBoardAlert}>
          <i className="fa-regular fa-message mr-2" />
          New Alert
        </DropdownMenuItem>

        <DropdownMenuItem disabled={user && ((user.isSandboxBoard && user.isDemoUser) || !user.can(GRANTS.grant_two))} onSelect={onAddNewBudget}>
          <i className="fa-light fa-coins mr-2" />
          New Budget
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <i className={`${SWIVU_STANDARD_ICONS[VIEW_TYPES.incentivePoints]} mr-2`} />
            New Points
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem disabled={!hasManagePermission} onSelect={onCreateNewOutcomeIncentive}>
              Outcome
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!hasManagePermission} onSelect={onCreateNewSpotIncentive}>
              Spot
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AppAddButton;
